// app/javascript/controllers/unsubscribe_validation_controller.js
import { Controller } from "@hotwired/stimulus"
import JustValidate from "just-validate"

export default class extends Controller {
    static targets = ["email", "error"]

    connect() {
        this.form = this.element

        this.emailEl = this.hasEmailTarget
            ? this.emailTarget
            : document.getElementById("unsubscribe_email")

        this.errorEl = this.hasErrorTarget
            ? this.errorTarget
            : document.getElementById("unsubscribe-error")

        // Listen for captcha success dispatched by recaptcha_bridge.js
        this.onCaptchaSuccess = (e) => {
            const token = e?.detail?.token
            this.submitAfterCaptcha(token)
        }
        this.form.addEventListener("captcha:success", this.onCaptchaSuccess)

        // JustValidate setup
        this.validator = new JustValidate(this.form, {
            validateBeforeSubmitting: true
        })

        this.validator.addField("#unsubscribe_email", [
            { rule: "required", errorMessage: "Email is required" },
            { rule: "email", errorMessage: "Please enter a valid email address" }
        ])

        // Create invisible reCAPTCHA widget (uses global callback -> recaptcha_bridge.js)
        this.recaptchaEl = document.querySelector("#unsubscribe-recaptcha")
        if (!this.recaptchaEl) return

        if (
            window.grecaptcha &&
            typeof window.grecaptcha.render === "function" &&
            typeof window.grecaptcha.execute === "function"
        ) {
            this.widgetId = window.grecaptcha.render("unsubscribe-recaptcha", {
                sitekey: this.recaptchaEl.dataset.sitekey,
                size: "invisible",
                callback: window.onUnsubCaptchaSuccess
            })
        } else {
            console.warn(
                "reCAPTCHA not ready yet (render/execute unavailable); will render on submit."
            )
        }
    }

    disconnect() {
        this.validator?.destroy()

        if (this.form && this.onCaptchaSuccess) {
            this.form.removeEventListener("captcha:success", this.onCaptchaSuccess)
        }

        if (this._recaptchaWaitTimer) {
            clearInterval(this._recaptchaWaitTimer)
            this._recaptchaWaitTimer = null
        }
    }

    waitForRecaptcha(maxMs = 4000, intervalMs = 100) {
        return new Promise((resolve) => {
            const startedAt = Date.now()

            // already loaded (ensure required APIs exist)
            if (
                window.grecaptcha &&
                typeof window.grecaptcha.render === "function" &&
                typeof window.grecaptcha.execute === "function"
            ) {
                return resolve(true)
            }

            this._recaptchaWaitTimer = setInterval(() => {
                if (
                    window.grecaptcha &&
                    typeof window.grecaptcha.render === "function" &&
                    typeof window.grecaptcha.execute === "function"
                ) {
                    clearInterval(this._recaptchaWaitTimer)
                    this._recaptchaWaitTimer = null
                    resolve(true)
                    return
                }

                if (Date.now() - startedAt >= maxMs) {
                    clearInterval(this._recaptchaWaitTimer)
                    this._recaptchaWaitTimer = null
                    resolve(false)
                }
            }, intervalMs)
        })
    }

    async submit(event) {
        event.preventDefault()

        const valid = await this.validator.revalidate().catch(() => false)
        if (!valid) {
            this.showError("Please enter a valid email address.")
            return
        }

        // Wait briefly for reCAPTCHA to load (it may load after connect())
        const loaded = await this.waitForRecaptcha()
        if (!loaded) {
            this.showError("Captcha is still loading. Please wait a moment and try again.")
            return
        }

        // If reCAPTCHA loaded after connect(), render it now
        if (this.widgetId === undefined && this.recaptchaEl) {
            this.widgetId = window.grecaptcha.render("unsubscribe-recaptcha", {
                sitekey: this.recaptchaEl.dataset.sitekey,
                size: "invisible",
                callback: window.onUnsubCaptchaSuccess
            })
        }

        if (this.widgetId === undefined) {
            this.showError("Captcha could not be initialized. Please refresh and try again.")
            return
        }

        window.grecaptcha.reset(this.widgetId)
        window.grecaptcha.execute(this.widgetId)
    }

    submitAfterCaptcha(token) {
        if (!token) {
            this.showError("CAPTCHA verification failed. Please try again.")
            return
        }

        // token is automatically added by reCAPTCHA to the form
        this.showLoader()
        this.form.submit()
    }

    showLoader() {
        const loader = this.application.getControllerForElementAndIdentifier(
            this.form,
            "form-loader"
        )
        loader?.showLoader()
    }

    showError(msg) {
        if (!this.errorEl) return
        this.errorEl.textContent = msg
        this.errorEl.classList.remove("d-none")
    }

    hideError() {
        if (!this.errorEl) return
        this.errorEl.classList.add("d-none")
        this.errorEl.textContent = ""
    }
}